<template>
  <v-card>
    <v-dialog v-model="uploadDialog" width="500" persistent>
      <v-card>
        <v-card-title
          class="grey lighten-2"
          primary-title
        >
          {{ $t('components.CreateAdHocModel.uploading') }} {{ !!file ? file.name : '' }}
        </v-card-title>

        <v-card-text v-if="!!file && !! uploadFileProgress">
          <v-progress-linear v-model="uploadFileProgress.percent" height="25" reactive class="mt-5">
            <strong>{{ uploadFileProgress.percent }}% ({{ `${fileSizeToString(uploadFileProgress.size)}/${fileSizeToString(file.size)}` }})</strong>
          </v-progress-linear>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="uploadFileProgress.task.cancel(); uploadDialog = false"
          >
            {{ $t('components.CreateAdHocModel.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ !!editing ? $t('components.CreateAdHocModel.editTitle') : $t('components.CreateAdHocModel.newTitle') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn dark text @click="triggerEdit" v-if="editing">{{ $t('components.CreateAdHocModel.save') }}</v-btn>
        <v-btn dark text @click="triggerCreate" :loading="creating" v-else>{{ $t('components.CreateAdHocModel.create') }}</v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-form ref="form" v-model="validForm">
      <v-card-text>
        <v-container>
          <v-alert v-if="uploadError" type="error" dismissible @input="uploadError = null">{{ uploadError }}</v-alert>
          <v-row>
            <v-col cols="6" sm="12" md="6">
              <v-text-field v-model="codename" :rules="rules.codename" :label="$t('components.CreateAdHocModel.modelCodename')" required @keydown.space.prevent :disabled="this.editing != null"></v-text-field>
            </v-col>
            <v-col cols="6" sm="12" md="6">
              <v-text-field v-model="name" :rules="rules.name" :label="$t('components.CreateAdHocModel.modelName')" required></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="12" md="12">
              <v-textarea v-model="json" outlined :rules="rules.json" :label="$t('components.CreateAdHocModel.json')" required :auto-grow="false" rows="14" />
            </v-col>
          </v-row>
          <v-row v-if="!editing">
            <v-col cols="12" sm="12" md="6">
              <div><h2>{{ $t('components.CreateAdHocModel.version') }}</h2></div>
              <model-version-fields v-model="version" :models="models" class="mt-2" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" sm="6" md="6">
              <div><h2>{{ $t('components.CreateAdHocModel.stages') }}</h2></div>
              <div v-for="stage in stages" v-bind:key="stage.name">
                <v-checkbox
                  v-model="selectedStages[stage.name]"
                  :label="stage.name"
                ></v-checkbox>
              </div>
            </v-col>
            <v-col cols="6" sm="6" md="6">
              <div><h2>{{ $t('components.CreateAdHocModel.settings') }}</h2></div>
              <div>
                <v-checkbox
                  v-model="enableDataUploader"
                  :label="$t('components.CreateAdHocModel.enableDataUploader')"
                ></v-checkbox>
              </div>
              <div>
                <v-text-field v-model="slackChannel" :label="$t('components.CreateAdHocModel.slackChannel')"></v-text-field>
              </div>
              <div>
                <v-checkbox
                  v-model="enableAI"
                  :label="$t('components.CreateAdHocModel.enableAISummary')"
                ></v-checkbox>
              </div>
              <div>
                <v-checkbox
                  v-model="enableImpressionsModel"
                  :label="$t('components.CreateAdHocModel.impressionsModel')"
                ></v-checkbox>
              </div>
              <div>
                <v-checkbox
                  v-model="useDifferentConfigForPlans"
                  :label="$t('components.CreateAdHocModel.useDifferentConfigForPlans')"
                ></v-checkbox>
              </div>
              <div v-if="authUser.isAdmin">
                <v-select
                  v-model="optimizerGpu"
                  :items="optimizerGpuOptions"
                  :label="$t('components.CreateAdHocModel.optimizerGpu')"
                  :hint="$t('components.CreateAdHocModel.optimizerGpuHint')"
                  persistent-hint
                ></v-select>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <h3 v-if="useDifferentConfigForPlans">{{ $t('components.CreateAdHocModel.modelReportPkl') }}</h3>
              <h3 v-else>{{ $t('components.CreateAdHocModel.modelPkl') }}</h3>
            </v-col>
          </v-row>
          <v-row v-if="editing && pklUrl">
            <v-col>
              <a :href="pklUrl" v-if="useDifferentConfigForPlans">{{ $t('components.CreateAdHocModel.downloadModelReportPkl') }}</a>
              <a :href="pklUrl" v-else>{{ $t('components.CreateAdHocModel.downloadPkl') }}</a>
            </v-col>
          </v-row>
          <v-row v-if="editing && !pklUrl">
            <v-col>
              {{ $t('components.CreateAdHocModel.noPklAvailable') }}
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <input v-show="false" type="file" accept=".pkl" @change="previewFile" id="file" ref="file" />
              <v-btn color="primary" dark small @click="selectFile">
                {{ $t('components.CreateAdHocModel.choosePklFile') }}
              </v-btn>
              <span class="ml-5" v-if="file">{{ !!file ? file.name : '' }} ({{ fileSizeToString(file.size) }})</span>
              <br>
              <div v-if="!editing && !this.file" style="color: #ff5252; margin-top: 5px; margin-left: 2px;">{{ $t('components.CreateAdHocModel.pklRequired') }}</div>
            </v-col>
          </v-row>
          <div v-if="useDifferentConfigForPlans">
            <v-row>
              <v-col>
                <h3>{{ $t('components.CreateAdHocModel.plansConfiguration') }}</h3>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="12" md="12">
                <v-textarea v-model="planJson" outlined :rules="rules.planJson" :label="$t('components.CreateAdHocModel.plansJson')" :auto-grow="false" rows="14" />
                <div v-if="useDifferentConfigForPlans && !planJson" style="color: #ff5252; margin-top: 5px; margin-left: 2px;">{{ $t('components.CreateAdHocModel.plansJsonRequired') }}</div>
              </v-col>
            </v-row>
            <v-row v-if="editing && planPklUrl">
              <v-col>
                <a :href="planPklUrl">{{ $t('components.CreateAdHocModel.downloadPlansPkl') }}</a>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <h3>{{ $t('components.CreateAdHocModel.plansPkl') }}</h3>
              </v-col>
            </v-row>
            <v-row v-if="editing && !planPklUrl">
              <v-col>
                {{ $t('components.CreateAdHocModel.noPlansPklAvailable') }}
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <input v-show="false" type="file" accept=".pkl" @change="previewPlanFile" id="planFile" ref="planFile" />
                <v-btn color="secondary" dark small @click="selectPlanFile">
                  {{ $t('components.CreateAdHocModel.choosePlansPklFile') }}
                </v-btn>
                <span class="ml-5" v-if="planFile">{{ !!planFile ? planFile.name : '' }} ({{ fileSizeToString(planFile.size) }})</span>
                <br>
                <div v-if="useDifferentConfigForPlans && !planFile && (!editing || !planPklUrl)" style="color: #ff5252; margin-top: 5px; margin-left: 2px;">{{ $t('components.CreateAdHocModel.plansPklRequired') }}</div>
              </v-col>
            </v-row>
          </div>
        </v-container>
      </v-card-text>
    </v-form>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import numbro from 'numbro'
import ModelVersionFields from '@/components/ModelVersionFields.vue'

export default {
  components: {
    ModelVersionFields
  },
  data () {
    return {
      validForm: true,
      json: null,
      codename: null,
      name: null,
      file: null,
      uploadDialog: false,
      useDifferentConfigForPlans: false,
      planJson: null,
      planFile: null,
      pklUrl: null,
      planPklUrl: null,
      stages: [
        { name: 'Kick-off', type: 'checkpoint', ng: true },
        { name: 'Sanity Check', type: 'file', ng: true },
        { name: 'Sanity Check Review', type: 'checkpoint' },
        { name: 'Comparison Report', type: 'file', ng: true },
        { name: 'Comparison Review', type: 'checkpoint' },
        { name: 'Feasibility Report', type: 'file', ng: true },
        { name: 'Feasibility Review', type: 'checkpoint' },
        { name: 'Marketing Report', type: 'file', ng: true },
        { name: 'Marketing Review', type: 'checkpoint' },
        { name: 'Checkout', type: 'file', ng: true },
        { name: 'Checkout Review', type: 'checkpoint' },
        { name: 'Deployment', type: 'checkpoint', ng: true }
      ],
      selectedStages: {},
      enableDataUploader: true,
      slackChannel: '',
      enableAI: false,
      enableImpressionsModel: false,
      optimizerGpu: 't4',
      version: { familyId: null, notes: '' },
      creating: false,
      uploadError: null
    }
  },
  methods: {
    ...mapActions('admin', [
      'createMmmModel',
      'editMmmModel',
      'setModelVersion'
    ]),
    ...mapActions('storage', [
      'uploadFile',
      'resetUploadFileProgress',
      'getFileDownloadUrl'
    ]),
    closeDialog () {
      this.$emit('close-dialog')
    },
    validateForm () {
      const formValid = this.$refs.form.validate()

      // Additional validation for planFile when useDifferentConfigForPlans is enabled
      if (this.useDifferentConfigForPlans) {
        const planFileRequired = !this.planFile && (!this.editing || !this.planPklUrl)
        if (planFileRequired) {
          return false
        }
      }

      return formValid
    },
    // Uploads the chosen PKLs; resolves false if one fails or is cancelled, so the model isn't saved
    // pointing at a file that never reached Storage.
    async uploadFiles (failedMessageKey) {
      this.uploadError = null
      const uploads = []
      if (this.file) {
        uploads.push({ file: this.file, upload: this.triggerUploadFile })
      }
      if (this.useDifferentConfigForPlans && this.planFile) {
        uploads.push({ file: this.planFile, upload: this.triggerUploadPlanFile })
      }
      for (const { file, upload } of uploads) {
        try {
          await upload()
        } catch (error) {
          console.error('Error uploading ' + file.name, error)
          this.uploadDialog = false
          if (error.code !== 'storage/canceled') {
            this.uploadError = this.$t('components.CreateAdHocModel.' + failedMessageKey, { file: file.name, error: error.message })
          }
          return false
        }
      }
      return true
    },
    async triggerCreate () {
      if (this.validateForm()) {
        if (!await this.uploadFiles('uploadFailedCreate')) {
          return
        }
        this.creating = true
        await this.createMmmModel({
          createdBy: this.createdBy,
          json: this.json ? this.json : null,
          codename: this.codename,
          name: this.name,
          pkl: this.file ? this.file : null,
          enabledStages: this.selectedStages,
          stages: this.stages,
          enableDataUploader: this.enableDataUploader,
          enableAI: this.enableAI,
          enableImpressionsModel: this.enableImpressionsModel,
          slackChannel: this.slackChannel.length > 0 ? this.slackChannel : null,
          useDifferentConfigForPlans: this.useDifferentConfigForPlans,
          planJson: this.useDifferentConfigForPlans ? this.planJson : null,
          planPkl: this.useDifferentConfigForPlans && this.planFile ? this.planFile : null,
          // Only admins choose it, so for anyone else the field is left out and stays untouched.
          optimizerGpu: this.authUser.isAdmin ? this.optimizerGpu : undefined
        }).then(() => {
          if (this.version.familyId) {
            // The link is made once the doc exists; a failure leaves the model created but unlinked.
            return this.setModelVersion({
              modelId: this.codename,
              familyId: this.version.familyId,
              notes: this.version.notes
            }).catch(error => {
              console.error('Error linking model version', error)
              this.$emit('version-link-failed', { name: this.name, error })
            })
          }
        }).catch(error => {
          console.log(error)
        })
        this.creating = false
        this.closeDialog()
      }
    },
    async triggerEdit () {
      if (this.validateForm()) {
        // New files are uploaded first; the DB is updated only if they all reached Storage
        if (!await this.uploadFiles('uploadFailedEdit')) {
          return
        }

        this.editMmmModel({
          editedBy: this.createdBy,
          json: this.json,
          codename: this.codename,
          name: this.name,
          pkl: this.file || this.editing.reference_model.pkl,
          enabledStages: this.selectedStages,
          stages: this.stages,
          enableDataUploader: this.enableDataUploader,
          enableAI: this.enableAI,
          enableImpressionsModel: this.enableImpressionsModel,
          slackChannel: this.slackChannel.length > 0 ? this.slackChannel : null,
          useDifferentConfigForPlans: this.useDifferentConfigForPlans,
          planJson: this.useDifferentConfigForPlans ? this.planJson : null,
          planPkl: this.useDifferentConfigForPlans && this.planFile ? this.planFile : (this.editing.plan_reference_model ? this.editing.plan_reference_model.pkl : null),
          optimizerGpu: this.authUser.isAdmin ? this.optimizerGpu : undefined
        })
        this.closeDialog()
      } else {
        console.log('Cannot validate form')
      }
    },
    selectFile: function () {
      this.$refs.file.click()
    },
    previewFile: function () {
      this.file = this.$refs.file.files[0]
    },
    selectPlanFile: function () {
      this.$refs.planFile.click()
    },
    previewPlanFile: function () {
      this.planFile = this.$refs.planFile.files[0]
    },
    triggerUploadFile: function () {
      return new Promise((resolve, reject) => {
        this.uploadDialog = true
        this.uploadFile({
          file: this.file,
          path: `models/mmm/${this.codename}.pkl`
        }).then(() => {
          this.uploadDialog = false
          resolve()
        }, error => {
          reject(error)
        })
      })
    },
    triggerUploadPlanFile: function () {
      return new Promise((resolve, reject) => {
        this.uploadDialog = true
        this.uploadFile({
          file: this.planFile,
          path: `models/mmm/${this.codename}_plan.pkl`
        }).then(() => {
          this.uploadDialog = false
          resolve()
        }, error => {
          reject(error)
        })
      })
    },
    fileSizeToString: function (size) {
      return numbro(size).format({
        output: 'byte',
        base: 'binary',
        mantissa: 1
      })
    }
  },
  computed: {
    ...mapGetters('storage', [
      'uploadFileProgress'
    ]),
    ...mapGetters('auth', [
      'authUser'
    ]),
    optimizerGpuOptions () {
      return [
        { text: this.$t('components.CreateAdHocModel.gpuT4'), value: 't4' },
        { text: this.$t('components.CreateAdHocModel.gpuA100'), value: 'a100' }
      ]
    },
    rules () {
      return {
        codename: [
          v => !!v || this.$t('components.CreateAdHocModel.codenameRequired'),
          v => (!!v && !!v.match(/^[0-9a-zA-Z_-]+$/)) || this.$t('components.CreateAdHocModel.codenameInvalid'),
          v => (!!this.editing || !this.existingModels.includes(v)) || this.$t('components.CreateAdHocModel.codenameExists')
        ],
        name: [
          v => !!v || this.$t('components.CreateAdHocModel.nameRequired')
        ],
        json: [
          // v => !!v || 'JSON is required',
          v => !v || this.validJson || this.$t('components.CreateAdHocModel.invalidJson')
        ],
        planJson: [
          v => !this.useDifferentConfigForPlans || !!v || this.$t('components.CreateAdHocModel.plansJsonRequired'),
          v => !v || this.validPlanJson || this.$t('components.CreateAdHocModel.invalidPlansJson')
        ],
        planFile: [
          () => !this.useDifferentConfigForPlans || !!this.planFile || (!this.editing && this.$t('components.CreateAdHocModel.plansPklRequired')) || (this.editing && !this.planPklUrl && this.$t('components.CreateAdHocModel.plansPklRequired'))
        ]
      }
    },
    validJson () {
      try {
        return (JSON.parse(this.json) && !!this.json)
      } catch (e) {
        return false
      }
    },
    validPlanJson () {
      if (!this.planJson) return true
      try {
        return (JSON.parse(this.planJson) && !!this.planJson)
      } catch (e) {
        return false
      }
    }
  },
  props: {
    createdBy: {
      type: String,
      required: true
    },
    editing: {
      type: Object,
      required: false
    },
    // Every Ad-Hoc model, to pick the family a new model is a version of.
    models: {
      type: Array,
      required: false,
      default: function () {
        return []
      }
    },
    existingModels: {
      type: Array,
      required: false,
      default: function () {
        return []
      }
    }
  },
  watch: {
    editing: {
      immediate: true,
      handler: function () {
        if (this.editing) {
          var model = this.editing.reference_model
          this.codename = model.codename
          this.name = this.editing.name
          this.json = JSON.stringify(model.config)
          if (this.editing.enabledStages) {
            this.selectedStages = this.editing.enabledStages
          }
          if (this.editing.stages) {
            for (const stageEditing in this.editing.stages) {
              for (const stage in this.stages) {
                if (this.editing.stages[stageEditing].name === this.stages[stage].name) {
                  this.stages[stage] = this.editing.stages[stageEditing]
                }
              }
            }
          }
          if (this.editing.enableDataUploader) {
            this.enableDataUploader = this.editing.enableDataUploader
          }
          if (this.editing.enableAI) {
            this.enableAI = this.editing.enableAI
          }
          if (this.editing.enableImpressionsModel) {
            this.enableImpressionsModel = this.editing.enableImpressionsModel
          }
          // A GPU no longer offered (2× T4, which ngtorch never used past the first) shows as the
          // default, so saving the model replaces it.
          if (this.editing.optimizer_gpu && this.optimizerGpuOptions.some(o => o.value === this.editing.optimizer_gpu)) {
            this.optimizerGpu = this.editing.optimizer_gpu
          }
          if (this.editing.slackChannel) {
            this.slackChannel = this.editing.slackChannel
          }
          if (this.editing.plan_reference_model) {
            this.useDifferentConfigForPlans = true
            this.planJson = JSON.stringify(this.editing.plan_reference_model.config)
          }
        }
      }
    }
  },
  created: function () {
    this.resetUploadFileProgress()
    if (this.editing) {
      this.getFileDownloadUrl({
        path: `/models/mmm/${this.codename}.pkl`
      }).then(url => {
        this.pklUrl = url
      }).catch(error => {
        console.log('PKL file not found in storage:', error)
        this.pklUrl = null
      })
      if (this.editing.plan_reference_model) {
        this.getFileDownloadUrl({
          path: `/models/mmm/${this.codename}_plan.pkl`
        }).then(url => {
          this.planPklUrl = url
        }).catch(error => {
          console.log('Plan PKL file not found in storage:', error)
          this.planPklUrl = null
        })
      }
    } else {
      // Only set default selected stages when creating a new model
      this.stages.forEach(stage => {
        this.selectedStages[stage.name] = false
      })
    }
  }
}
</script>
